/**
 * One-time orientation tour for the Accessibility Settings screen.
 *
 * Shown the first time an employee visits `/accessibility`. Spotlights three
 * controls in sequence: text-size slider, bold-text toggle, apply button.
 *
 * Same two-layer seen-check as AppTour:
 *   1. Local store (`app-tour:v1`, keyed by employeeId).
 *   2. Server-hydrated stage (`stages[ACCESSIBILITY_TOUR_SUB_MODULE_ID]?.guided`).
 *
 * Step content is read from the `player` i18n namespace (`accessibilityTour.*`)
 * so it follows the `?lang=` URL param.
 */

import { Bold, CheckCircle2, Type } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { useLessonProgress } from '@/player/lesson-progress'
import { getSessionIdentity } from '@/session/identity'
import {
  getElementRect,
  postTourCompletion,
  type Rect,
  SCROLL_SETTLE_MS,
  SHOW_DELAY_MS,
  Spotlight,
  TourCard,
  type TourStepMeta,
} from './tour-overlay-shared'
import { ACCESSIBILITY_TOUR_SUB_MODULE_ID, APP_TOUR_MODULE_ID, useAppTour } from './use-app-tour'

// ─── Step metadata ────────────────────────────────────────────────────────────

const STEP_META: readonly TourStepMeta[] = [
  { id: 'textSize', target: 'a11y-text-size', Icon: Type },
  { id: 'boldText', target: 'a11y-bold-text', Icon: Bold },
  { id: 'apply', target: 'a11y-apply-btn', Icon: CheckCircle2 },
]

const STEP_IDS = STEP_META.map((s) => s.id)

// ─── Component ────────────────────────────────────────────────────────────────

export function AccessibilityTour() {
  const { t } = useTranslation('player')
  const location = useLocation()
  const isOnScreen = location.pathname === '/accessibility'

  const seenBy = useAppTour((s) => s.seenBy)
  const markSeen = useAppTour((s) => s.markSeen)
  const stages = useLessonProgress((s) => s.stages)
  const identity = getSessionIdentity()

  const alreadySeen = useMemo(
    () =>
      (identity !== null && seenBy[`a11y:${identity.employeeId}`] === true) ||
      stages[ACCESSIBILITY_TOUR_SUB_MODULE_ID]?.guided !== undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identity, seenBy, stages],
  )

  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [spotlightRect, setSpotlightRect] = useState<Rect | null>(null)

  const meta = STEP_META[stepIndex]

  useEffect(() => {
    if (alreadySeen || dismissed || !isOnScreen) return
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [alreadySeen, dismissed, isOnScreen])

  useEffect(() => {
    if (!isOnScreen) setVisible(false)
  }, [isOnScreen])

  useEffect(() => {
    if (!visible || !meta?.target) {
      setSpotlightRect(null)
      return
    }
    const el = document.querySelector(`[data-app-tour="${meta.target}"]`)
    if (!el) {
      setSpotlightRect(null)
      return
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    const timer = setTimeout(
      () => setSpotlightRect(getElementRect(meta.target ?? '')),
      SCROLL_SETTLE_MS,
    )
    return () => clearTimeout(timer)
  }, [visible, meta?.target])

  const dismiss = (completed: boolean) => {
    setVisible(false)
    setDismissed(true)
    if (identity) markSeen(`a11y:${identity.employeeId}`)
    if (completed) {
      useLessonProgress.getState().hydrateStages([
        {
          subModuleId: ACCESSIBILITY_TOUR_SUB_MODULE_ID,
          moduleId: APP_TOUR_MODULE_ID,
          completedAt: new Date().toISOString(),
          stage: 'guided',
          requiresUnguided: false,
        },
      ])
      postTourCompletion(APP_TOUR_MODULE_ID, ACCESSIBILITY_TOUR_SUB_MODULE_ID)
    }
  }

  const handleNext = () => {
    if (stepIndex < STEP_META.length - 1) setStepIndex((i) => i + 1)
    else dismiss(true)
  }
  const handleBack = () => setStepIndex((i) => Math.max(0, i - 1))
  const handleSkip = () => dismiss(false)

  if (!visible || alreadySeen || dismissed || !meta) return null

  return (
    <div className="fixed inset-0" style={{ zIndex: 9900 }}>
      {spotlightRect ? (
        <Spotlight rect={spotlightRect} />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-black/75" />
      )}

      <TourCard
        Icon={meta.Icon}
        title={t(`accessibilityTour.${meta.id}.title`)}
        body={t(`accessibilityTour.${meta.id}.body`)}
        stepIndex={stepIndex}
        totalSteps={STEP_META.length}
        stepIds={STEP_IDS}
        spotlightRect={spotlightRect}
        lastStepLabel={t('tour.gotIt')}
        onBack={handleBack}
        onNext={handleNext}
        onSkip={handleSkip}
      />
    </div>
  )
}
